/**
 * Sample Button Controller for Pillovely
 *
 * Handles variant changes, button states, and cart interactions.
 *
 * Adapted for themePreLive's `<variant-picker>` component and
 * `theme.pubsub` cart event bus (themeLive uses `variant-radios` /
 * `variant-selects` and a global `subscribe`/`publish` pair instead).
 *
 * @version 1.0.0
 */

(() => {
  'use strict';

  // =============================================================================
  // CONSTANTS & CONFIGURATION
  // =============================================================================

  const CONFIG = Object.freeze({
    DEBOUNCE_DELAY: 150,
    ERROR_DISPLAY_DURATION: 3000,
    MAX_SAMPLES: 5,
    SAMPLE_IDENTIFIERS: Object.freeze(['próbnik', 'probnik', 'sample', 'próbka', 'probka', 'muster']),
    DEBUG: false,
    CART_OBSERVER_DELAY: 150,
    VARIANT_CHANGE_DELAY: 100
  });

  const CART_ENDPOINTS = Object.freeze([
    '/cart/add.js',
    '/cart/change.js',
    '/cart/update.js',
    '/cart/clear.js'
  ]);

  // =============================================================================
  // LOGGING UTILITIES
  // =============================================================================

  const logger = {
    log: (...args) => CONFIG.DEBUG && console.log('[SampleButtonController]', ...args),
    warn: (...args) => console.warn('[SampleButtonController]', ...args)
  };

  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================

  const debounce = (fn, delay) => {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Accept': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${url}`);
    }

    return response.json();
  };

  // =============================================================================
  // CART OBSERVER (SINGLETON)
  // =============================================================================

  const CartObserver = (() => {
    let observer = null;
    let isObserving = false;
    let processingRequest = false;
    const callbacks = new Set();

    const isCartRequest = (entry) => {
      if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') {
        return false;
      }
      return CART_ENDPOINTS.some(endpoint => entry.name.includes(endpoint));
    };

    const handleCartRequest = async () => {
      if (processingRequest) return;

      processingRequest = true;

      setTimeout(async () => {
        try {
          const cart = await fetchJson('/cart.js');
          logger.log(`Cart updated, notifying ${callbacks.size} subscriber(s)`);

          callbacks.forEach(callback => {
            try {
              callback(cart);
            } catch (error) {
              logger.warn('Callback error:', error);
            }
          });
        } catch (error) {
          logger.warn('Error handling cart request:', error);
        } finally {
          processingRequest = false;
        }
      }, CONFIG.CART_OBSERVER_DELAY);
    };

    const start = () => {
      if (isObserving || !window.PerformanceObserver) {
        if (!window.PerformanceObserver) {
          logger.warn('PerformanceObserver not supported, falling back to events only');
        }
        return;
      }

      logger.log('Starting cart request observer');

      try {
        observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (isCartRequest(entry)) {
              logger.log('Detected cart request:', entry.name);
              handleCartRequest();
            }
          }
        });

        observer.observe({ entryTypes: ['resource'] });
        isObserving = true;
      } catch (error) {
        logger.warn('Failed to start observer:', error);
      }
    };

    const stop = () => {
      if (observer) {
        logger.log('Stopping observer');
        observer.disconnect();
        observer = null;
        isObserving = false;
      }
    };

    const subscribe = (callback) => {
      callbacks.add(callback);
      start();
      logger.log(`Subscriber added, total: ${callbacks.size}`);
    };

    const unsubscribe = (callback) => {
      callbacks.delete(callback);
      logger.log(`Subscriber removed, total: ${callbacks.size}`);

      if (callbacks.size === 0) {
        stop();
      }
    };

    return Object.freeze({ subscribe, unsubscribe });
  })();

  // =============================================================================
  // SAMPLE BUTTON CONTROLLER CLASS
  // =============================================================================

  class SampleButtonController {
    constructor(wrapper) {
      // DOM references
      this.wrapper = wrapper;
      this.button = wrapper.querySelector('.product-sample-button');

      // Configuration
      this.sectionId = wrapper.dataset.sectionId;
      this.productId = wrapper.dataset.productId;
      this.locale = wrapper.dataset.locale ||
        window.Shopify?.locale ||
        document.documentElement.lang?.substring(0, 2) ||
        'pl';
      this.fabricOptionIndex = this.parseFabricOptionIndex(wrapper.dataset.fabricOptionIndex);
      this.fabricOptionName = wrapper.dataset.fabricOptionName || '';

      // Parse translations
      this.translations = this.parseTranslations(wrapper.dataset.translations);

      // Cache button elements
      this.elements = this.cacheElements();

      // pubsub unsubscribe handles (theme.pubsub.subscribe returns one)
      this.cartUpdateUnsubscriber = null;
      this.optionChangeUnsubscriber = null;

      // State management
      this.state = {
        isLoading: false,
        isInitializing: true,
        currentFabricValue: this.button?.dataset.fabricValue || '',
        currentSample: null,
        sampleLimitReached: false,
        currentSampleInCart: false,
        samplesInCart: 0,
        errorTimeout: null
      };

      // Bind methods
      this.handleClick = this.handleClick.bind(this);
      this.handleVariantChange = debounce(
        this.handleVariantChange.bind(this),
        CONFIG.DEBOUNCE_DELAY
      );
      this.handleCartUpdate = this.handleCartUpdate.bind(this);

      this.init();
    }

    // =========================================================================
    // INITIALIZATION
    // =========================================================================

    parseFabricOptionIndex(value) {
      const index = parseInt(value, 10);
      if (isNaN(index) || index < 0) {
        return -1;
      }
      logger.log('Fabric option config:', { index, name: this.fabricOptionName });
      return index;
    }

    parseTranslations(jsonString) {
      try {
        return JSON.parse(jsonString || '{}');
      } catch (error) {
        logger.warn('Failed to parse translations:', error);
        return {};
      }
    }

    cacheElements() {
      if (!this.button) return {};

      return {
        text: this.button.querySelector('.product-sample-button__text'),
        loading: this.button.querySelector('.product-sample-button__loading'),
        success: this.button.querySelector('.product-sample-button__success'),
        error: this.button.querySelector('.product-sample-button__error'),
        fabricName: this.button.querySelector('.product-sample-button__fabric-name')
      };
    }

    async init() {
      if (!this.button) {
        logger.warn('Sample button not found in wrapper');
        return;
      }

      logger.log('Initializing sample button controller for section:', this.sectionId);

      // Disable button immediately to prevent flashing between states
      this.button.disabled = true;

      this.button.addEventListener('click', this.handleClick);
      this.setupVariantChangeListener();
      this.setupCartUpdateListener();

      await this.prefetchCurrentSample();
      await this.checkSampleLimit(this.getCurrentSampleVariantId());

      // Initialization complete, enable visual updates
      this.state.isInitializing = false;

      // Determine final button state after all checks complete
      if (this.state.sampleLimitReached) {
        this.setLimitReachedState();
        logger.log('Init complete: limit reached');
      } else if (this.state.currentSampleInCart) {
        this.setSampleAlreadyInCartState();
        logger.log('Init complete: current sample already in cart');
      } else if (!this.state.currentSample || !this.isSampleAvailable(this.state.currentSample)) {
        this.setSampleUnavailableState();
        logger.log('Init complete: sample unavailable');
      } else {
        // Everything is OK, enable the button
        this.button.disabled = false;
        this.elements.text.hidden = false
        logger.log('Init complete: button enabled');
      }
    }

    // =========================================================================
    // CART MANAGEMENT
    // =========================================================================

    setupCartUpdateListener() {
      // Shopify theme editor section reloads
      document.addEventListener('shopify:section:load', this.handleCartUpdate);

      // themePreLive's cart bus (assets/theme.js: theme.pubsub)
      if (window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.handleCartUpdate
        );
      }

      // Storage events
      window.addEventListener('storage', (e) => {
        if (e.key?.includes('cart')) {
          this.handleCartUpdate();
        }
      });

      // Generic fallback: detect any /cart/*.js network request
      CartObserver.subscribe(this.handleCartUpdate);
    }

    async handleCartUpdate() {
      logger.log('Cart updated, rechecking sample limit');
      await this.checkSampleLimit(this.getCurrentSampleVariantId());
    }

    async checkSampleLimit(currentSampleVariantId = null) {
      try {
        const cart = await fetchJson('/cart.js');
        const sampleCount = this.countSamplesInCart(cart);
        const currentSampleInCart = currentSampleVariantId
          ? this.isSampleVariantInCart(cart, currentSampleVariantId)
          : false;
        const wasLimitReached = this.state.sampleLimitReached;
        const wasCurrentSampleInCart = this.state.currentSampleInCart;

        this.state.samplesInCart = sampleCount;
        this.state.sampleLimitReached = sampleCount >= CONFIG.MAX_SAMPLES;
        this.state.currentSampleInCart = currentSampleInCart;

        logger.log('Sample limit check:', {
          samplesInCart: sampleCount,
          maxSamples: CONFIG.MAX_SAMPLES,
          currentSampleVariantId,
          currentSampleInCart,
          limitReached: this.state.sampleLimitReached,
          wasLimitReached
        });

        if (this.state.sampleLimitReached) {
          this.setLimitReachedState();
        } else if (this.state.currentSampleInCart) {
          this.setSampleAlreadyInCartState();
        } else if (wasLimitReached || wasCurrentSampleInCart) {
          this.updateNormalAvailability();
        }

        return {
          count: sampleCount,
          limitReached: this.state.sampleLimitReached,
          currentSampleInCart: this.state.currentSampleInCart
        };
      } catch (error) {
        logger.warn('Error checking sample limit:', error);
        return { count: 0, limitReached: false, currentSampleInCart: false };
      }
    }

    countSamplesInCart(cart) {
      if (!cart?.items) return 0;

      const sampleCount = cart.items.reduce((count, item) => {
        return count + (this.isSampleProduct(item) ? item.quantity : 0);
      }, 0);

      logger.log(`Found ${sampleCount} sample(s) in cart`);
      return sampleCount;
    }

    isSampleVariantInCart(cart, variantId) {
      if (!cart?.items || !variantId) return false;

      return cart.items.some(item => {
        const itemVariantId = item.variant_id || item.id;
        return this.isSampleProduct(item) && String(itemVariantId) === String(variantId);
      });
    }

    isSampleProduct(item) {
      const searchFields = [
        (item.product_title || item.title || '').toLowerCase(),
        (item.product_type || '').toLowerCase(),
        (item.vendor || '').toLowerCase()
      ];

      return searchFields.some(field =>
        CONFIG.SAMPLE_IDENTIFIERS.some(identifier => field.includes(identifier))
      );
    }

    // =========================================================================
    // VARIANT MANAGEMENT
    // =========================================================================

    // themePreLive's ProductInfo replaces the whole <variant-picker> node on
    // every option change (theme.js: ProductInfo.updateOptionValues ->
    // theme.HTMLUpdateUtility.viewTransition), so this must NOT be cached -
    // a cached reference goes stale (detached from the document) the moment
    // the first option change happens, and every check against it afterwards
    // silently fails.
    getVariantPicker() {
      return (
        document.getElementById(`VariantPicker-${this.sectionId}-${this.productId}`) ||
        document.querySelector(`variant-picker[id^="VariantPicker-${this.sectionId}-"]`)
      );
    }

    setupVariantChangeListener() {
      if (!this.getVariantPicker()) {
        logger.warn(`No <variant-picker> found for VariantPicker-${this.sectionId}-${this.productId}.`);
      }

      // Since the <variant-picker> node itself gets replaced on every
      // change (see getVariantPicker() above), we can't rely on a DOM
      // 'change' listener attached to it - that listener dies with the old
      // node after the first change. Instead we use the same signal
      // themePreLive's own price/media sync relies on: theme.pubsub's
      // optionValueSelectionChange, published by <variant-picker> itself
      // and unaffected by the node being swapped out afterwards. Match by
      // the picker's id (stable across replacements), not node identity.
      if (window.theme?.pubsub?.PUB_SUB_EVENTS?.optionValueSelectionChange) {
        const expectedPickerId = `VariantPicker-${this.sectionId}-${this.productId}`;

        this.optionChangeUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          (payload) => {
            const changedPicker = payload?.data?.target?.closest?.('variant-picker');
            if (changedPicker?.id && changedPicker.id !== expectedPickerId) return;
            this.handleVariantChange();
          }
        );
      }

      // URL changes
      window.addEventListener('popstate', () => {
        setTimeout(() => this.handleVariantChange(), CONFIG.VARIANT_CHANGE_DELAY);
      });
    }

    async handleVariantChange() {
      logger.log('Variant change detected');

      const newFabricValue = this.getCurrentFabricValue();

      if (newFabricValue === this.state.currentFabricValue) {
        logger.log('Fabric value unchanged:', newFabricValue);
        return;
      }

      this.state.currentFabricValue = newFabricValue;
      this.state.currentSample = null;
      this.state.currentSampleInCart = false;

      logger.log('Fabric value changed to:', newFabricValue);

      await this.updateButtonState(newFabricValue);
    }

    getCurrentFabricValue() {
      const optionIndex = this.fabricOptionIndex;

      if (optionIndex < 0) {
        logger.log('No fabric option index, using fallback');
        return this.button?.dataset.fabricValue || '';
      }

      // Try the <variant-picker> option group (fieldset for swatches/buttons,
      // div wrapper for the dropdown picker type)
      const variantPicker = this.getVariantPicker();

      if (variantPicker) {
        const group = variantPicker.querySelector(`[data-option-index="option${optionIndex + 1}"]`);

        if (group) {
          const checkedInput = group.querySelector('input:checked');
          if (checkedInput) {
            logger.log(`Got fabric value from option group[${optionIndex}] input:`, checkedInput.value);
            return checkedInput.value;
          }

          const select = group.querySelector('select');
          if (select) {
            logger.log(`Got fabric value from option group[${optionIndex}] select:`, select.value);
            return select.value;
          }
        }
      }

      // Try URL variant
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');
      if (variantId) {
        const variantData = this.getVariantData();
        const variant = variantData?.find(v => v.id == variantId);
        if (variant) {
          const optionKey = `option${optionIndex + 1}`;
          if (variant[optionKey]) {
            logger.log(`Got fabric value from variant data[${optionKey}]:`, variant[optionKey]);
            return variant[optionKey];
          }
        }
      }

      return this.button?.dataset.fabricValue || '';
    }

    getVariantData() {
      const variantPicker = this.getVariantPicker();
      const scriptTag = variantPicker?.querySelector('script[data-variants]');

      if (scriptTag) {
        try {
          return JSON.parse(scriptTag.textContent);
        } catch (error) {
          logger.warn('Failed to parse variant data:', error);
        }
      }

      return null;
    }

    // =========================================================================
    // SAMPLE MANAGEMENT
    // =========================================================================

    async prefetchCurrentSample() {
      if (this.state.currentFabricValue && window.SampleMapper) {
        await this.prefetchSample(this.state.currentFabricValue);
      }
    }

    async prefetchSample(fabricValue) {
      if (!window.SampleMapper) {
        logger.warn('SampleMapper not available');
        return;
      }

      try {
        logger.log('Pre-fetching sample for:', fabricValue);
        const sample = await window.SampleMapper.getSampleForVariant(fabricValue, this.locale);

        if (sample && fabricValue === this.state.currentFabricValue) {
          this.state.currentSample = sample;
          logger.log('Pre-fetched sample:', sample.title);

          if (!this.isSampleAvailable(sample)) {
            logger.log('Sample found but unavailable (out of stock)');
            this.setSampleUnavailableState();
          }
        } else if (!sample && fabricValue === this.state.currentFabricValue) {
          logger.log('No matching sample found for:', fabricValue);
          this.setSampleUnavailableState();
        }
      } catch (error) {
        logger.warn('Error pre-fetching sample:', error);
      }
    }

    isSampleAvailable(sample) {
      if (!sample) return false;

      if (sample.variants?.length > 0) {
        return sample.variants[0].available === true;
      }

      return sample.available === true;
    }

    getSampleVariantId(sample) {
      return sample?.variants?.[0]?.id || sample?.id || null;
    }

    getCurrentSampleVariantId() {
      return this.getSampleVariantId(this.state.currentSample);
    }

    // =========================================================================
    // BUTTON STATE MANAGEMENT
    // =========================================================================

    async updateButtonState(fabricValue) {
      if (!fabricValue) {
        this.setUnavailableState();
        return;
      }

      this.button.classList.remove('limit-reached', 'success', 'error', 'sample-unavailable');
      this.updateButtonText(fabricValue);

      // Keep the button in a loading state while we check availability and
      // cart contents, so we don't flash "Order Sample" before immediately
      // swapping to "Already in cart" / "Limit reached" once the checks land.
      this.setLoadingState(true);

      await this.prefetchSample(fabricValue);
      await this.checkSampleLimit(this.getCurrentSampleVariantId());

      this.setLoadingState(false);
      this.updateNormalAvailability();
    }

    updateButtonText(fabricValue) {
      if (this.elements.text) {
        if (fabricValue) {
          this.elements.text.innerHTML =
            this.translations.buttonTextWithFabric +
            `<span class="product-sample-button__fabric-name">${fabricValue}</span>`;
        } else {
          this.elements.text.textContent = this.translations.unavailableText;
        }
      } else if (this.elements.fabricName) {
        this.elements.fabricName.textContent = fabricValue;
      }

      this.button.dataset.fabricValue = fabricValue;
      this.button.setAttribute('aria-label',
        `${this.translations.buttonText} ${fabricValue}`
      );
    }

    setLoadingState(loading) {
      this.state.isLoading = loading;
      this.button.classList.toggle('loading', loading);
      this.button.setAttribute('aria-disabled', loading ? 'true' : 'false');

      if (this.elements.text) this.elements.text.hidden = loading;
      if (this.elements.loading) this.elements.loading.hidden = !loading;
      if (this.elements.success) this.elements.success.hidden = true;
      if (this.elements.error) this.elements.error.hidden = true;
    }

    setErrorState() {
      if (this.state.errorTimeout) {
        clearTimeout(this.state.errorTimeout);
        this.state.errorTimeout = null;
      }

      this.setLoadingState(false);
      this.button.classList.add('error');

      if (this.elements.text) this.elements.text.hidden = true;
      if (this.elements.loading) this.elements.loading.hidden = true;
      if (this.elements.error) this.elements.error.hidden = false;

      this.state.errorTimeout = setTimeout(() => {
        this.button.classList.remove('error');
        if (this.elements.text) this.elements.text.hidden = false;
        if (this.elements.error) this.elements.error.hidden = true;
        this.button.setAttribute('aria-disabled', 'false');
        this.state.errorTimeout = null;
      }, CONFIG.ERROR_DISPLAY_DURATION);
    }

    setLimitReachedState() {
      // Skip visual updates during initialization to prevent flickering
      if (this.state.isInitializing) {
        logger.log('Skipping limit state update during init');
        return;
      }

      if (this.state.errorTimeout) {
        clearTimeout(this.state.errorTimeout);
        this.state.errorTimeout = null;
      }

      this.setLoadingState(false);

      if (this.elements.loading) this.elements.loading.hidden = true;
      if (this.elements.success) this.elements.success.hidden = true;
      if (this.elements.error) this.elements.error.hidden = true;

      if (this.elements.text) {
        this.elements.text.hidden = false;
        this.elements.text.textContent = this.translations.limitReachedText;
      }

      this.button.disabled = true;
      this.button.classList.add('limit-reached');
      this.button.classList.remove('success', 'error', 'loading');

      logger.log('Sample limit reached, button disabled');
    }

    setSampleAlreadyInCartState() {
      // Skip visual updates during initialization to prevent flickering
      if (this.state.isInitializing) {
        logger.log('Skipping sample-in-cart state update during init');
        return;
      }

      if (this.state.errorTimeout) {
        clearTimeout(this.state.errorTimeout);
        this.state.errorTimeout = null;
      }

      this.setLoadingState(false);

      if (this.elements.loading) this.elements.loading.hidden = true;
      if (this.elements.success) this.elements.success.hidden = true;
      if (this.elements.error) this.elements.error.hidden = true;

      if (this.elements.text) {
        this.elements.text.hidden = false;
        this.elements.text.textContent = this.translations.variantLimitText || this.translations.limitReachedText;
      }

      this.button.disabled = true;
      this.button.classList.add('limit-reached');
      this.button.classList.remove('success', 'error', 'loading');

      logger.log('Current sample already in cart, button disabled');
    }

    updateNormalAvailability() {
      if (this.state.sampleLimitReached) {
        this.setLimitReachedState();
      } else if (this.state.currentSampleInCart) {
        this.setSampleAlreadyInCartState();
      } else if (!this.state.currentSample || !this.isSampleAvailable(this.state.currentSample)) {
        this.setSampleUnavailableState();
      } else {
        this.restoreNormalState();
      }
    }

    restoreNormalState() {
      logger.log('Restoring normal button state');

      this.setLoadingState(false);
      this.button.classList.remove('limit-reached', 'success', 'error', 'loading', 'sample-unavailable');

      if (this.state.currentFabricValue) {
        this.button.disabled = false;
        this.elements.text.hidden = false
        this.updateButtonText(this.state.currentFabricValue);
      } else {
        this.setUnavailableState();
      }

      logger.log('Button restored to normal state');
    }

    setUnavailableState() {
      if (this.elements.text) {
        this.elements.text.textContent = this.translations.unavailableText;
      }
      this.button.disabled = true;
    }

    setSampleUnavailableState() {
      // Skip visual updates during initialization to prevent flickering
      if (this.state.isInitializing) {
        logger.log('Skipping unavailable state update during init');
        return;
      }

      if (this.elements.text) {
        this.elements.text.hidden = false;
        this.elements.text.textContent = this.translations.unavailableText;
      }
      this.button.disabled = true;
      this.button.classList.add('sample-unavailable');
      this.button.classList.remove('loading');

      logger.log('Sample unavailable, button disabled');
    }

    // =========================================================================
    // CLICK HANDLER
    // =========================================================================

    async handleClick(event) {
      event.preventDefault();

      if (this.state.isLoading || this.button.disabled) {
        return;
      }

      logger.log('Sample button clicked for:', this.state.currentFabricValue);

      this.setLoadingState(true);

      try {
        let sample = this.state.currentSample;

        if (!sample && window.SampleMapper) {
          sample = await window.SampleMapper.getSampleForVariant(
            this.state.currentFabricValue,
            this.locale
          );
        }

        if (!sample) {
          throw new Error('No matching sample found');
        }

        if (!this.isSampleAvailable(sample)) {
          logger.log('Sample found but unavailable (out of stock)');
          this.setSampleUnavailableState();
          return;
        }

        const variantId = this.getSampleVariantId(sample);

        if (!variantId) {
          throw new Error('No variant ID found for sample');
        }

        const { count, limitReached, currentSampleInCart } = await this.checkSampleLimit(variantId);

        if (limitReached) {
          logger.log('Sample limit reached, cannot add more');
          this.setLimitReachedState();
          return;
        }

        if (currentSampleInCart) {
          logger.log('Current sample already in cart, cannot add another');
          this.setSampleAlreadyInCartState();
          return;
        }

        logger.log('Adding sample to cart:', sample.title);

        await window.SampleMapper.addSampleToCart(variantId);

        this.state.samplesInCart = count + 1;
        this.state.currentSampleInCart = true;

        if (this.state.samplesInCart >= CONFIG.MAX_SAMPLES) {
          this.state.sampleLimitReached = true;
          this.setLimitReachedState();
        } else {
          this.setSampleAlreadyInCartState();
        }
      } catch (error) {
        logger.warn('Error adding sample to cart:', error);
        this.setErrorState();
      }
    }

    // =========================================================================
    // CLEANUP
    // =========================================================================

    destroy() {
      if (this.button) {
        this.button.removeEventListener('click', this.handleClick);
      }

      if (this.state.errorTimeout) {
        clearTimeout(this.state.errorTimeout);
        this.state.errorTimeout = null;
      }

      CartObserver.unsubscribe(this.handleCartUpdate);

      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }

      if (this.optionChangeUnsubscriber) {
        this.optionChangeUnsubscriber();
        this.optionChangeUnsubscriber = null;
      }

      document.removeEventListener('shopify:section:load', this.handleCartUpdate);

      logger.log('Controller destroyed');
    }
  }

  // =============================================================================
  // INITIALIZATION
  // =============================================================================

  const initializeSampleButtons = () => {
    const wrappers = document.querySelectorAll('.product-sample-wrapper');

    wrappers.forEach(wrapper => {
      if (wrapper.dataset.initialized === 'true') {
        return;
      }

      new SampleButtonController(wrapper);
      wrapper.dataset.initialized = 'true';
    });

    logger.log(`Initialized ${wrappers.length} sample button(s)`);
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeSampleButtons);
  } else {
    initializeSampleButtons();
  }

  // Re-initialize on Shopify section load
  document.addEventListener('shopify:section:load', () => {
    logger.log('Section loaded, re-initializing...');
    initializeSampleButtons();
  });

  // Export for external access
  window.SampleButtonController = SampleButtonController;
  window.initializeSampleButtons = initializeSampleButtons;
})();
